import { yupResolver } from '@hookform/resolvers/yup'
import { DefaultService } from '@LoopKitchen/typescript-client/lib/clients/FrontendDataService'
import { Close } from '@mui/icons-material'
import {
  Box,
  Dialog,
  IconButton,
  Popover,
  PopoverOrigin,
  useMediaQuery
} from '@mui/material'
import { captureException } from '@sentry/nextjs'
import Image from 'next/image'
import posthog from 'posthog-js'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import LoopLogo from 'src/assets/logos/loop_logo_short.svg'
import { CustomModalTypo } from 'src/components/mui/StyledTypo'
import { useSnackData } from 'src/context/SnackContext'
import theme from 'src/themes/defaultTheme'
import { sendSlackMessageToChannel } from 'src/utils/sendMessageToSlackChannel'
import * as yup from 'yup'
import EmailVerificationForm from './EmailVerificationForm'
import OtpVerificationForm from './OtpVerificationForm'

export interface EmailVerificationDialogValues {
  email: string
  name: string
  organization: string
  designation: string
}

const validationSchema = yup.object({
  email: yup
    .string()
    .email('Email format is invalid')
    .required('Email is required'),
  name: yup
    .string()
    .min(3, 'Minimum 3 characters are required')
    .required('Name is required'),
  organization: yup.string().required('Organization is required'),
  designation: yup.string().required('Designation is required')
})

type EmailVerificationDialogProps = {
  open: boolean
  handleClose: () => void
  component?: 'popover' | 'dialog'
} & (
  | {
      component: 'popover'
      anchorEle: HTMLElement
      anchorOrigin?: PopoverOrigin
      transformOrigin?: PopoverOrigin
    }
  | {
      component?: 'dialog'
    }
)

const EmailVerificationDialog = (props: EmailVerificationDialogProps) => {
  const { open, handleClose, component } = props
  const {
    anchorEle = null,
    anchorOrigin = null,
    transformOrigin = null
  } = component === 'popover' ? props : {}
  const sm = useMediaQuery(theme.breakpoints.down('sm'))
  const { openSuccess, openError } = useSnackData()

  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [loading, setLoading] = useState(false)

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { errors, isValid, isDirty }
  } = useForm<EmailVerificationDialogValues>({
    defaultValues: {
      email: '',
      name: '',
      organization: '',
      designation: ''
    },
    resolver: yupResolver(validationSchema),
    mode: 'all'
  })

  async function onSubmit(values: EmailVerificationDialogValues) {
    setLoading(true)
    try {
      const { name, email, organization, designation } = values

      const res = await DefaultService.sendEmailOtpSendEmailOtpPost({
        email,
        name,
        company: organization
        // TODO: Add designation to the API
      })

      if (res.success) {
        openSuccess(res.message)
        setStep('otp')
        posthog.capture('FC100 Register', {
          name,
          email,
          company: organization,
          designation
        })
        sendSlackMessageToChannel({
          message: `FC100 Register`,
          title: `FC 100 Registration Details:\t
          Name: ${name}\t
          Email: ${email}\t
          Company: ${organization}\t
          Designation: ${designation}`,
          channel: '#new-inbound-leads'
        })
      } else {
        openError(res.message)
      }
    } catch (error) {
      console.log(error)
      captureException(error)
      openError('Failed to send verification email')
    }
    setLoading(false)
  }

  const isPopover = useMemo(() => {
    return component === 'popover'
  }, [component])

  const closeDialog = () => {
    handleClose()
    reset()
    setStep('email')
  }

  const content = (
    <Box
      bgcolor="white"
      padding={sm ? 2 : 4}
      position="relative"
      sx={{
        maxWidth: 'fit-content',
        minWidth: { xs: '100%', md: '700px' },
        height: {
          xs: '100vh',
          md: 'auto'
        },
        overflowY: 'auto'
      }}>
      <Box display="flex">
        <IconButton
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            color: 'black'
          }}
          onClick={closeDialog}>
          <Close color="inherit" />
        </IconButton>

        <Box display="flex" width={'100%'} gap={5}>
          <Box
            sx={{
              flex: 2,
              height: '100%',
              bgcolor: 'rgb(18, 41, 55)',
              borderRadius: 1,
              p: 1,
              display: { xs: 'none', md: 'flex' }
            }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '100%',
                background: `url('/background_signup.png')`,
                py: 5,
                px: 2
              }}>
              <Box></Box>
              <CustomModalTypo c={'#FFFFFF'}>
                Trusted by{' '}
                <span
                  style={{
                    fontFamily: 'Sora',
                    color: '#55C4FF'
                  }}>
                  4000+
                </span>{' '}
                locations across USA
              </CustomModalTypo>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 3,
              py: 2,
              px: { xs: 2, sm: 5, md: 2 }
            }}>
            <Box
              sx={{
                height: '100%'
              }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: '5px' }}>
                <Image src={LoopLogo} alt="logo" width={30} height={30} />
                <Image
                  src="/img/logos/fast_casual_logo.svg"
                  alt="Fast Casual Logo"
                  width={127}
                  height={44}
                  style={{ height: '30px' }}
                />
              </Box>

              {step === 'email' ? (
                <EmailVerificationForm
                  control={control}
                  errors={errors}
                  handleSubmit={handleSubmit(onSubmit)}
                  disabled={!isDirty || !isValid || loading}
                />
              ) : (
                <OtpVerificationForm
                  handleResubmit={handleSubmit(onSubmit)}
                  email={getValues('email')}
                  closeDialog={closeDialog}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )

  return isPopover ? (
    <Popover
      disableScrollLock
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      open={open}
      onClose={closeDialog}
      anchorEl={anchorEle}
      transformOrigin={transformOrigin || undefined}
      anchorOrigin={anchorOrigin || undefined}>
      {content}
    </Popover>
  ) : (
    <Dialog
      open={open}
      onClose={closeDialog}
      fullScreen={sm}
      PaperProps={{
        sx: {
          position: 'fixed',
          top: 0,
          maxWidth: '100%',
          maxHeight: {
            xs: '100vh',
            sm: '90vh',
            lg: '95vh'
          }
        }
      }}>
      {content}
    </Dialog>
  )
}

export default EmailVerificationDialog
